import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { format, parseISO } from 'date-fns';

import Collapse from '@/components/ui-bridge/core/Collapse';
import Grid from '@/components/ui-bridge/core/Grid';
import PropTypes from 'prop-types';

import { cn } from '@/lib/utils';
import classes from './ReportHistory.module.scss';
import Download from '@/svg-icons/Download';
import ExpandLess from '@/svg-icons/ExpandLess';
import ExpandMore from '@/svg-icons/ExpandMore';
import History from '@/svg-icons/History';
import Shield from '@/svg-icons/Shield';
import { isReportEligibleForSignature } from '@/helpers/signatureOrderHelpers';
import ReportSignatureAction from './ReportSignatureAction';

const ReportHistory = ({ data, orderId, orderStatus }) => {
  const reports = Object.values(data).sort((a, b) => parseFloat(b.version) - parseFloat(a.version));
  const [open, setOpen] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const intl = useIntl();

  let moreButton = null;

  const showTypeHandler = (version, show) => {
    const openStatus = { ...open };
    openStatus[version] = show;

    setOpen(openStatus);
  };

  const showMoreHandler = show => {
    setIsExpanded(show);
  };

  const formatReportVersion = reportData => {
    if (['divergence_v2', 'full_divergence', 'simple_divergence', 'divergence_general'].includes(reportData.type)) {
      if (reportData.tags && reportData.tags.length) {
        const partyMap = {
          TENANT: intl.formatMessage({ id: 'DIVERGENCE_REPORT_TENANT', defaultMessage: 'Relatório de contestação (Inquilino)' }),
          OWNER: intl.formatMessage({ id: 'DIVERGENCE_REPORT_OWNER', defaultMessage: 'Relatório de contestação (Proprietário)' }),
          LANDLORD: intl.formatMessage({ id: 'DIVERGENCE_REPORT_OWNER', defaultMessage: 'Relatório de contestação (Proprietário)' }),
          REAL_ESTATE: intl.formatMessage({ id: 'DIVERGENCE_REPORT_REAL_ESTATE', defaultMessage: 'Relatório de contestação (Imobiliária)' }),
          OTHER: intl.formatMessage({ id: 'DIVERGENCE_REPORT_OTHER', defaultMessage: 'Relatório de contestação (Outros)' }),
        };
        const tag = String(reportData.tags[0]).toUpperCase();
        if (partyMap[tag]) {
          return partyMap[tag];
        }
      }
      return `${intl.formatMessage({ id: 'DIVERGENCE_REPORT', defaultMessage: 'Relatório de contestação' })}`;
    }
    if (
      reportData.type === 'full_inspection' &&
      reportData.evaluations !== undefined &&
      reportData.evaluations.length === 1 &&
      reportData.evaluations[0] === 'Reprovado'
    ) {
      return `${intl.formatMessage({ id: 'REJECTION_REPORT' })}`;
    }
    if (reportData.type === 'full_inspection' && reportData.evaluations !== undefined) {
      return `${intl.formatMessage({ id: 'DAMAGE_REPORT' })}`;
    }
    const { tags } = reportData;
    const [modality, type] = reportData.type.split('_');
    let format;
    if (modality === 'fc') {
      format = intl.formatMessage({ id: 'fichaCerta' });
    } else {
      format =
        modality === 'full' ? intl.formatMessage({ id: 'WITH_PHOTOS' }) : intl.formatMessage({ id: 'WITHOUT_PHOTOS' });
    }
    let verboseType;
    let verboseTags;
    switch (type) {
      case 'report':
        verboseType = '';
        break;
      case 'damages':
        verboseTags = tags ? tags.map(tag => intl.formatMessage({ id: `TAG.${tag.toUpperCase()}` })).join(', ') : null;
        verboseType = intl.formatMessage({ id: 'NON_CONFORMITIES' });
        verboseType = verboseTags ? `${verboseType} (${verboseTags.replace(/,([^,]*)$/, ' e$1')})` : verboseType;
        break;
      default: {
        const reportTypeKey = `REPORT_TYPE.${type}`;
        verboseType =
          reportTypeKey in intl.messages
            ? intl.formatMessage({ id: reportTypeKey })
            : type in intl.messages
            ? intl.formatMessage({ id: type })
            : type.charAt(0).toUpperCase() + type.slice(1);
      }
    }
    return `${intl.formatMessage({ id: 'REPORT' })} ${verboseType ? `${verboseType} ` : ''}${format}`;
  };

  const showHashHandler = async (event, hash) => {
    try {
      await navigator.clipboard.writeText(hash);
      alert(intl.formatMessage({ id: 'INTEGRITY_CODE_COPIED' }));
    } catch (err) {
      alert(intl.formatMessage({ id: 'INTEGRITY_CODE_COPY_FAILED' }, { hash }));
      console.error('Async: Could not copy text: ', err);
    }
  };

  if (reports.length > 1) {
    if (isExpanded === false) {
      moreButton = (
        <button type="button" className={classes.ShowMoreButton} onClick={() => showMoreHandler(true)}>
          <FormattedMessage id="SHOW_MORE" />
        </button>
      );
    } else {
      moreButton = (
        <button type="button" className={classes.ShowMoreButton} onClick={() => showMoreHandler(false)}>
          <FormattedMessage id="SHOW_LESS" />
        </button>
      );
    }
  }

  return (
    <Grid item xs={12}>
      <div className={classes.Header}>
        <History viewBox="0 0 34.016 34.016" className={classes.Icon} />
        <FormattedMessage id="report_history" />
      </div>
      <div className={classes.ListWrapper}>
        <ul>
          {reports.map((version, index) => {
            const versionName =
              index === 0 ? `${version.version} (${intl.formatMessage({ id: 'ACTUAL' })})` : version.version;

            let versionComponent = (
              <li key={version.hash} data-cy={`order-details-report-${version.hash}-version`}>
                <p
                  className={cn(classes.VersionTitle, index === 0 && 'font-bold')}
                  data-cy={`order-details-report-${version.hash}-title`}
                >
                  <span>{`${intl.formatMessage({
                    id: 'VERSION',
                  })} ${versionName}`}</span>
                  <span data-cy={`order-details-report-${version.hash}-date`}>
                    {format(parseISO(version.date), 'dd/MM - HH:mm')}
                  </span>
                  <span className={classes.ListAction}>
                    {open[version.version] ? (
                      <ExpandLess viewBox="0 0 22 22" onClick={() => showTypeHandler(version.version, false)} />
                    ) : (
                      <ExpandMore viewBox="0 0 22 22" onClick={() => showTypeHandler(version.version, true)} />
                    )}
                  </span>
                </p>
                <Collapse in={open[version.version] === true} timeout="auto" unmountOnExit>
                  <ul data-cy={`order-details-report-${version.hash}-files`}>
                    {version?.files?.map(file => (
                      <li key={file.hash}>
                        <p>
                          <span>
                            {formatReportVersion(file)}
                            {file.auto_translate === 'en-US' &&
                              ` (${intl.formatMessage({ id: 'REPORT.TRANSLATED_EN' })})`}
                          </span>
                          <span style={{ margin: '0 .5em', whiteSpace: 'nowrap' }}>
                            {format(parseISO(file.date), 'dd/MM - HH:mm')}
                          </span>
                          <span className={classes.ListAction}>
                            <a href={file.file} target="_blank" rel="noopener noreferrer">
                              <Download viewBox="0 0 24 24" />
                            </a>
                            <Shield
                              viewBox="0 0 24 24"
                              className={classes.IntegrityCode}
                              onClick={e => showHashHandler(e, file.hash)}
                            />
                            {/*TODO waiting production approval
                              orderStatus === 'FINISHED' && orderId && isReportEligibleForSignature(file) && (
                              <ReportSignatureAction orderId={orderId} reportHash={file.hash} />
                            )*/}
                          </span>
                        </p>
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </li>
            );
            if (index !== 0) {
              versionComponent = (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit key={`collapsed-${version.hash}`}>
                  {versionComponent}
                </Collapse>
              );
            }
            return versionComponent;
          })}
        </ul>
        {moreButton}
      </div>
    </Grid>
  );
};

ReportHistory.propTypes = {
  data: PropTypes.object.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orderStatus: PropTypes.string,
};

ReportHistory.defaultProps = {
  orderId: null,
  orderStatus: null,
};

export default ReportHistory;
